import { useRef, useEffect } from 'react'
import { Chart } from 'chart.js'
import { useThemeStore } from '../../stores/themeStore'

interface InventoryDataPoint {
    period: string
    totalQuantity: number
    uniqueSKUs: number
    inventoryValue: number
}

interface Props {
    data: InventoryDataPoint[]
    selectedSnapshot?: string
    onSelectSnapshot?: (snapshot: string) => void
    settings?: {
        showUnits?: boolean
        showSKUs?: boolean
        showValue?: boolean
        showTooltipYoY?: boolean
        showTooltipMoM?: boolean
        forceZeroYAxis?: boolean
    }
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

const METRIC_COLORS = {
    units: { line: 'rgba(168, 85, 247, 1)', fill: 'rgba(168, 85, 247, 0.25)' },
    skus:  { line: 'rgba(59, 130, 246, 1)',  fill: 'rgba(59, 130, 246, 0.15)' },
    value: { line: 'rgba(16, 185, 129, 1)',  fill: 'rgba(16, 185, 129, 0.15)' },
}

/**
 * Theme-aware multi-metric area chart for inventory levels over time.
 * Supports toggling between Units on Hand, Unique SKUs, and Inventory Value.
 */
export default function InventoryLevelChart({ data, selectedSnapshot, onSelectSnapshot, settings }: Props) {
    const canvasRef = useRef<HTMLCanvasElement>(null)
    const chartRef = useRef<Chart | null>(null)

    const showUnits = settings?.showUnits !== false
    const showSKUs = settings?.showSKUs === true
    const showValue = settings?.showValue === true

    const colorMode = useThemeStore(s => s.getEffectiveColorMode())
    const textColor = colorMode === 'dark' ? 'rgba(255,255,255,0.6)' : '#475569'

    const isPointActive = (index: number) => {
        if (!selectedSnapshot) return false
        const pt = data[index]
        if (!pt) return false
        if (selectedSnapshot === 'LIVE') {
            return pt.period === 'Live'
        }
        return pt.period === selectedSnapshot || pt.period.startsWith(selectedSnapshot) || selectedSnapshot.startsWith(pt.period)
    }
    const gridColor = colorMode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'
    const tooltipBg = colorMode === 'dark' ? 'rgba(15,10,30,0.95)' : 'rgba(255,255,255,0.95)'
    const tooltipTitle = colorMode === 'dark' ? '#fff' : '#0f172a'
    const tooltipBody = colorMode === 'dark' ? 'rgba(255,255,255,0.8)' : '#334155'
    const tooltipBorder = colorMode === 'dark' ? 'rgba(168, 85, 247, 0.3)' : 'rgba(168, 85, 247, 0.15)'
    const pointBorder = colorMode === 'dark' ? '#1e1b2e' : '#fff'

    const labels = data.map(d => {
        if (d.period === 'Live') return '● Live'
        const [y, m] = d.period.split('-')
        return `${MONTHS[parseInt(m) - 1]} ${String(y).slice(-2)}`
    })

    // Determine which metrics need a second Y axis (value uses currency scale)
    const hasValue = showValue
    const hasCountMetric = showUnits || showSKUs

    useEffect(() => {
        if (!canvasRef.current) return

        if (chartRef.current) {
            chartRef.current.destroy()
            chartRef.current = null
        }

        const ctx = canvasRef.current.getContext('2d')
        if (!ctx) return

        const datasets: any[] = []

        if (showUnits) {
            const gradient = ctx.createLinearGradient(0, 0, 0, canvasRef.current.height)
            gradient.addColorStop(0, METRIC_COLORS.units.fill)
            gradient.addColorStop(1, 'rgba(168, 85, 247, 0.02)')
            datasets.push({
                label: 'Units on Hand',
                data: data.map(d => d.totalQuantity),
                borderColor: METRIC_COLORS.units.line,
                backgroundColor: gradient,
                borderWidth: 2.5,
                pointRadius: (ctx: any) => isPointActive(ctx.dataIndex) ? 7 : (data.length > 24 ? 0 : 4),
                pointHoverRadius: (ctx: any) => isPointActive(ctx.dataIndex) ? 9 : 6,
                pointBackgroundColor: (ctx: any) => isPointActive(ctx.dataIndex) ? (colorMode === 'dark' ? '#c084fc' : '#a855f7') : METRIC_COLORS.units.line,
                pointBorderColor: (ctx: any) => isPointActive(ctx.dataIndex) ? (colorMode === 'dark' ? '#fff' : '#1e1b2e') : pointBorder,
                pointBorderWidth: (ctx: any) => isPointActive(ctx.dataIndex) ? 3 : 2,
                fill: true,
                tension: 0.35,
                yAxisID: 'y',
            })
        }

        if (showSKUs) {
            const gradient = ctx.createLinearGradient(0, 0, 0, canvasRef.current.height)
            gradient.addColorStop(0, METRIC_COLORS.skus.fill)
            gradient.addColorStop(1, 'rgba(59, 130, 246, 0.02)')
            datasets.push({
                label: 'Unique SKUs',
                data: data.map(d => d.uniqueSKUs),
                borderColor: METRIC_COLORS.skus.line,
                backgroundColor: gradient,
                borderWidth: 2,
                pointRadius: (ctx: any) => isPointActive(ctx.dataIndex) ? 7 : (data.length > 24 ? 0 : 3),
                pointHoverRadius: (ctx: any) => isPointActive(ctx.dataIndex) ? 9 : 5,
                pointBackgroundColor: (ctx: any) => isPointActive(ctx.dataIndex) ? (colorMode === 'dark' ? '#c084fc' : '#a855f7') : METRIC_COLORS.skus.line,
                pointBorderColor: (ctx: any) => isPointActive(ctx.dataIndex) ? (colorMode === 'dark' ? '#fff' : '#1e1b2e') : pointBorder,
                pointBorderWidth: (ctx: any) => isPointActive(ctx.dataIndex) ? 3 : 2,
                fill: true,
                tension: 0.35,
                yAxisID: 'y',
            })
        }

        if (showValue) {
            const gradient = ctx.createLinearGradient(0, 0, 0, canvasRef.current.height)
            gradient.addColorStop(0, METRIC_COLORS.value.fill)
            gradient.addColorStop(1, 'rgba(16, 185, 129, 0.02)')
            datasets.push({
                label: 'Inventory Value ($)',
                data: data.map(d => d.inventoryValue),
                borderColor: METRIC_COLORS.value.line,
                backgroundColor: gradient,
                borderWidth: 2,
                pointRadius: (ctx: any) => isPointActive(ctx.dataIndex) ? 7 : (data.length > 24 ? 0 : 3),
                pointHoverRadius: (ctx: any) => isPointActive(ctx.dataIndex) ? 9 : 5,
                pointBackgroundColor: (ctx: any) => isPointActive(ctx.dataIndex) ? (colorMode === 'dark' ? '#c084fc' : '#a855f7') : METRIC_COLORS.value.line,
                pointBorderColor: (ctx: any) => isPointActive(ctx.dataIndex) ? (colorMode === 'dark' ? '#fff' : '#1e1b2e') : pointBorder,
                pointBorderWidth: (ctx: any) => isPointActive(ctx.dataIndex) ? 3 : 2,
                fill: true,
                tension: 0.35,
                yAxisID: hasCountMetric ? 'y1' : 'y',
            })
        }

        // Pre-calculate special indices that must be shown (first, last, and year transitions)
        const specialIndices = new Set<number>();
        specialIndices.add(0);
        specialIndices.add(labels.length - 1);
        for (let i = 1; i < labels.length; i++) {
            const curr = labels[i];
            const prev = labels[i - 1];
            if (curr && prev) {
                const currYear = curr.split(' ')[1];
                const prevYear = prev.split(' ')[1];
                if (currYear && prevYear && currYear !== prevYear && currYear !== 'Live' && prevYear !== 'Live') {
                    specialIndices.add(i);
                }
            }
        }

        // Find index of selected snapshot to make sure it is always displayed in ticks
        if (selectedSnapshot) {
            const activeIdx = data.findIndex((_, idx) => isPointActive(idx))
            if (activeIdx !== -1) {
                specialIndices.add(activeIdx)
            }
        }

        // Determine proximity threshold based on label density to prevent overlap
        const proximityThreshold = labels.length > 30 ? 2 : labels.length > 15 ? 1 : 0;

        const isCloseToSpecial = (idx: number) => {
            for (const specIdx of specialIndices) {
                if (specIdx !== idx && Math.abs(idx - specIdx) <= proximityThreshold) {
                    return true;
                }
            }
            return false;
        };

        const scales: any = {
            x: {
                grid: {
                    color: (context: any) => {
                        const idx = context.index;
                        if (idx === undefined || idx <= 0 || idx >= labels.length) return 'transparent';
                        if (specialIndices.has(idx) && idx !== 0 && idx !== labels.length - 1) {
                            if (labels[idx] !== '● Live') {
                                return colorMode === 'dark' ? 'rgba(255, 255, 255, 0.25)' : 'rgba(0, 0, 0, 0.25)';
                            }
                        }
                        return 'transparent';
                    },
                    lineWidth: (context: any) => {
                        const idx = context.index;
                        if (idx === undefined || idx <= 0 || idx >= labels.length) return 1;
                        if (specialIndices.has(idx) && idx !== 0 && idx !== labels.length - 1) {
                            if (labels[idx] !== '● Live') {
                                return 2;
                            }
                        }
                        return 1;
                    }
                },
                ticks: { 
                    color: (context: any) => {
                        const idx = context.tick?.value ?? context.index
                        if (idx !== undefined && isPointActive(idx)) {
                            return colorMode === 'dark' ? '#c084fc' : '#a855f7'
                        }
                        return textColor
                    }, 
                    font: (context: any) => {
                        const idx = context.tick?.value ?? context.index
                        const baseFont = { size: 10, weight: 'normal' as const }
                        if (idx !== undefined && isPointActive(idx)) {
                            return { ...baseFont, weight: 'bold' as const }
                        }
                        return baseFont
                    }, 
                    maxRotation: 0,
                    autoSkip: false,
                    callback: function(_value: any, index: number) {
                        if (specialIndices.has(index)) {
                            return labels[index];
                        }
                        
                        const step = Math.ceil(labels.length / 10);
                        if (index % step === 0 && !isCloseToSpecial(index)) {
                            return labels[index];
                        }
                        
                        return '';
                    }
                },
                border: { display: false },
                afterFit: (axis: any) => { axis.paddingRight = 20 },
            },
            y: {
                grid: { color: gridColor },
                ticks: {
                    color: textColor, font: { size: 10 },
                    callback: (v: any) => {
                        const n = Number(v)
                        if (!hasCountMetric && hasValue) {
                            // Value-only mode: format as currency
                            if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`
                            if (n >= 1_000) return `$${(n / 1_000).toFixed(0)}K`
                            return `$${n}`
                        }
                        if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`
                        if (n >= 1_000) return `${(n / 1_000).toFixed(0)}K`
                        return `${n}`
                    }
                },
                border: { display: false },
                beginAtZero: settings?.forceZeroYAxis === true,
            }
        }

        // Add secondary Y axis for value when a count metric is also showing
        if (hasCountMetric && hasValue) {
            scales.y1 = {
                position: 'right',
                grid: { drawOnChartArea: false },
                ticks: {
                    color: METRIC_COLORS.value.line, font: { size: 10 },
                    callback: (v: any) => {
                        const n = Number(v)
                        if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`
                        if (n >= 1_000) return `$${(n / 1_000).toFixed(0)}K`
                        return `$${n}`
                    }
                },
                border: { display: false },
                beginAtZero: settings?.forceZeroYAxis === true,
            }
        }

        chartRef.current = new Chart(ctx, {
            type: 'line',
            data: { labels, datasets },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                interaction: { mode: 'index', intersect: false },
                onClick: (event: any, elements: any[], chart: any) => {
                    let clickedIdx = -1

                    if (elements && elements.length > 0) {
                        clickedIdx = elements[0].index
                    } else if (chart) {
                        const active = chart.getElementsAtEventForMode(
                            event,
                            'index',
                            { intersect: false },
                            true
                        )
                        if (active && active.length > 0) {
                            clickedIdx = active[0].index
                        }
                    }

                    // Fallback to coordinates-based lookup (highly reliable even with pointRadius: 0)
                    if (clickedIdx === -1 && chart) {
                        const xAxis = chart.scales.x
                        if (xAxis) {
                            const idx = xAxis.getValueForPixel(event.x)
                            if (idx !== undefined && idx >= 0 && idx < data.length) {
                                clickedIdx = idx
                            }
                        }
                    }

                    if (clickedIdx !== -1 && onSelectSnapshot) {
                        const pt = data[clickedIdx]
                        if (pt) {
                            onSelectSnapshot(pt.period === 'Live' ? 'LIVE' : pt.period)
                        }
                    }
                },
                onHover: (event: any, elements: any[], chart: any) => {
                    if (event.native?.target && chart) {
                        const inArea = chart.chartArea && event.x >= chart.chartArea.left && event.x <= chart.chartArea.right;
                        event.native.target.style.cursor = (elements.length > 0 || inArea) ? 'pointer' : 'default'
                    }
                },
                plugins: {
                    legend: {
                        display: datasets.length > 1,
                        position: 'top', align: 'end',
                        labels: { color: textColor, font: { size: 11 }, boxWidth: 12, padding: 12, usePointStyle: true, pointStyle: 'circle' }
                    },
                    tooltip: {
                        backgroundColor: tooltipBg, titleColor: tooltipTitle, bodyColor: tooltipBody,
                        borderColor: tooltipBorder, borderWidth: 1, cornerRadius: 8, padding: 10,
                        callbacks: {
                            title: (items: any[]) => {
                                if (!items.length) return ''
                                const idx = items[0].dataIndex
                                const pt = data[idx]
                                if (pt.period === 'Live') return 'Live Inventory'
                                return labels[idx]
                            },
                            label: (item: any) => {
                                const val = item.parsed.y
                                if (val == null) return ''
                                const label = item.dataset.label || ''
                                if (label.includes('Value')) return `${label}: $${val.toLocaleString()}`
                                return `${label}: ${val.toLocaleString()}`
                            },
                            afterLabel: (item: any) => {
                                const val = item.parsed.y
                                if (val == null) return []
                                const label = item.dataset.label || ''
                                const idx = item.dataIndex
                                const pt = data[idx] as any
                                
                                let lyVal = 0
                                let lmVal = 0
                                if (label.includes('Units')) { lyVal = pt.lyTotalQuantity ?? 0; lmVal = pt.lmTotalQuantity ?? 0 }
                                else if (label.includes('SKUs')) { lyVal = pt.lyUniqueSKUs ?? 0; lmVal = pt.lmUniqueSKUs ?? 0 }
                                else if (label.includes('Value')) { lyVal = pt.lyInventoryValue ?? 0; lmVal = pt.lmInventoryValue ?? 0 }

                                let lines: string[] = []
                                const formatVal = (v: number) => label.includes('Value') ? `$${v.toLocaleString()}` : v.toLocaleString()

                                if (settings?.showTooltipMoM === true && lmVal > 0) {
                                    const pct = ((val - lmVal) / lmVal) * 100
                                    const indicator = pct > 0 ? '▲' : pct < 0 ? '▼' : ' '
                                    lines.push(`${indicator} ${Math.abs(pct).toFixed(1)}%   vs prior month (${formatVal(lmVal)})`)
                                }
                                if (settings?.showTooltipYoY !== false && lyVal > 0) {
                                    const pct = ((val - lyVal) / lyVal) * 100
                                    const indicator = pct > 0 ? '▲' : pct < 0 ? '▼' : ' '
                                    lines.push(`${indicator} ${Math.abs(pct).toFixed(1)}%   vs last year (${formatVal(lyVal)})`)
                                }
                                return lines
                            }
                        }
                    }
                },
                scales,
            }
        } as any)

        return () => {
            if (chartRef.current) { chartRef.current.destroy(); chartRef.current = null }
        }
    }, [data, showUnits, showSKUs, showValue, colorMode, settings?.forceZeroYAxis, settings?.showTooltipYoY, settings?.showTooltipMoM, selectedSnapshot, onSelectSnapshot])

    return (
        <div className="chart-panel" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <div className="chart-panel-title" style={{ padding: '12px 16px 0', fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)' }}>
                Inventory Levels Over Time
            </div>
            <div style={{ flex: 1, minHeight: 0, padding: '8px 12px 12px', position: 'relative' }}>
                <canvas ref={canvasRef} />
            </div>
        </div>
    )
}
